import { isEmpty } from '@ember/utils';
import dayjs from 'dayjs';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import { addNoDataZone, addZone, NO_DATA_ZONE } from 'lumen/utils/charts/zones';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';

export function getSeriesConfiguration(
  hotel,
  data,
  isCurrentHotel,
  activeCompetitors,
  zones = [],
) {
  return {
    hotelId: hotel.id,
    name: hotel.name,
    data,
    originalData: data,
    zoneAxis: 'x',
    zones,
    type: 'line',
    color: getChartColor(hotel.id, activeCompetitors),
    zIndex: isCurrentHotel ? 15 : 10,
    lineWidth: isCurrentHotel ? 5 : 2,
    marker: {
      enabled: true,
      radius: isCurrentHotel ? 5 : 4,
    },
  };
}

export function calculateYaxisMax(
  currentHotel,
  competitorsForCurrentHotelAndOTA,
  periods,
) {
  let max = 150;

  if (currentHotel?.loading) {
    return max;
  }

  const rates = [];
  const hotels = [currentHotel?.hotelinfo, ...competitorsForCurrentHotelAndOTA];

  hotels.forEach((hotel) => {
    periods.forEach((period) => {
      const rate = period.rates?.[hotel.id];

      if (rate !== undefined) {
        const selectedRate = rate[0];
        if (
          selectedRate &&
          !isEmpty(selectedRate.value) &&
          selectedRate.value !== 0
        ) {
          rates.push(selectedRate.value);
        }
      }
    });
  });

  // Sort the rates
  const sortedRates = rates.sort((a, b) => b - a);
  // Pick the rate at the 15th percentile
  const pointToUse = Math.floor((sortedRates.length - 1) * 0.15);
  const highestPointToUse = sortedRates[pointToUse];

  max = highestPointToUse * 2;

  // To make sure you don't have too much white space above the chart
  // This has been added because in the case of hostels,
  // there is a chance the highestPointToUse * 2 causes half the chart to be almost empty
  while (max > sortedRates[0] * 1.25) {
    max *= 0.75;
  }

  // If the max is too much just use the highest point
  max = max > sortedRates[0] ? sortedRates[0] : max;

  // Round to a multiple of 5
  // Always provide some margin (5 in this case)
  max = 5 * Math.ceil(max / 5);

  return max;
}

export function calculateTickPositions(
  currentHotel,
  competitorsForCurrentHotelAndOTA,
  periods,
  yAxisMax,
  yAxisMin,
  tickAmount,
) {
  if (currentHotel?.loading) {
    return undefined;
  }

  const max = yAxisMax;
  const hotels = [currentHotel?.hotelinfo, ...competitorsForCurrentHotelAndOTA];

  let min = yAxisMin ?? max;
  let nrSoldOut = 0;

  periods.forEach((period) => {
    hotels.forEach((hotel) => {
      const rate = period.rates?.[hotel.id];

      if (rate !== undefined) {
        const selectedRate = rate[0];
        if (
          selectedRate &&
          !isEmpty(selectedRate.value) &&
          selectedRate.value !== 0
        ) {
          if (selectedRate.value < min) {
            min = selectedRate.value;
          }
        } else {
          nrSoldOut += 1;
        }
      } else {
        nrSoldOut += 1;
      }
    });
  });

  // If everything is sold out, set min to zero
  if (nrSoldOut === hotels.length * periods?.length) {
    min = 0;
  }

  let tick;
  if (min === max) {
    // If min is max, ticks will be empty. Better to return null and let highcharts handle the placement (will be centered)
    return null;
  }

  if (min === 0) {
    tick = Math.ceil((max - min) / (tickAmount - 1));
  } else {
    // One tick below the minimum
    tick = Math.ceil((max - min) / (tickAmount - 2));
  }

  const ticks = [];
  for (let interval = 0; interval < tickAmount * tick; interval += tick) {
    // This will result in an array as follows: [max, max-tick, max-2*tick,..]
    ticks.push(max - interval);
  }

  // reverse the tickpositions
  return ticks.reverse();
}

export function connectRateDataPoint(
  hotelAndMarketService,
  xValue,
  xSoldOut,
  zones,
  selectedRate,
  yAxisMax,
  hotelId,
) {
  // There is a rate,
  // now we need to determine if the previous one was sold out
  // and connect it to the current rate
  const previousValueX = xValue;
  const lastZone = zones[zones.length - 1];

  // If dashStyle is dot, then previous point was sold out...
  if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
    addZone(zones, xSoldOut); // = period fromDate + 1 day
  }

  const outlier = selectedRate.value > yAxisMax;
  const yValue = outlier ? yAxisMax : selectedRate.value; // Set yValue top of chart if it's an outlier (way bigger than the rest)
  const previousValueY = yValue;
  const returnDict = {
    x: xValue,
    y: yValue,
    hotelId,
    tooltipValue: Number(selectedRate.value.toFixed(0)),
  };

  if (outlier) {
    returnDict.dataLabels = {
      enabled: true,
      formatter() {
        const label = hotelAndMarketService.formatRate(
          this.point.tooltipValue,
          false,
          false,
        );

        return label === null ? label : `${label}`;
      },
    };
  }

  return [returnDict, previousValueX, previousValueY];
}

export function connectUndefinedRateDataPoint(
  previousValueX,
  previousValueY,
  zones,
  xValue,
  xSoldOut,
  index,
  selectedRate,
  hotelId,
) {
  // There is no current rate or it is sold out, LOS2,...
  // Determine where the previous point was and connect to it,
  // displaying a cross on the current day.
  let prevValueX = previousValueX;
  if (prevValueX === 0) {
    prevValueX = xValue;
  }

  // If this is the first point, and there were no zones
  // Make a zone up to this point representing a solid line
  if (!zones.length && index > 0) {
    addZone(zones, previousValueX);
  }

  // Construct the next point (can only predict x-value)
  const nextPoint = {
    x: xSoldOut,
  };

  // Construct the previous point if it exists
  const previousPoint = {
    x: previousValueX,
    y: previousValueY,
  };

  addNoDataZone(zones, previousPoint, nextPoint, index);

  prevValueX = xValue;

  const returnDict = {
    x: prevValueX,
    y: previousValueY,
    tooltipValue: Number(selectedRate.value.toFixed(0)),
    hotelId,
    marker: HIGHCHARTS_MISSING_MARKER,
  };

  return [returnDict, prevValueX];
}

export function xAxistickPositionerForOverviewDashboard(dataMin, dataMax) {
  const positions = [];

  if (dataMax !== null && dataMin !== null) {
    const startDate = dayjs.unix(dataMin / 1000);

    const increment = startDate.add(1, 'week').unix() * 1000 - dataMin;

    for (
      let tick = Math.floor(dataMin);
      tick - increment < dataMax;
      tick += increment
    ) {
      positions.push(tick);
    }
  }

  return positions;
}
